/**
 * Trạng thái phòng.
 *
 * Màu để ngay đây chứ không rải trong component: trước kia RoomCard và
 * RoomDetailPage mỗi nơi tự tô một kiểu, nên cùng một phòng mà hai màn hiện hai
 * màu khác nhau - và khi gộp bảng màu toàn app thì hai nhánh khác nghĩa vô tình
 * bị trộn thành một màu mà không ai phát hiện ra.
 *
 * KHÔNG dùng xanh lá ở đây: xanh lá đã dành riêng cho "đã thanh toán".
 * `available` để trung tính là đúng nghĩa - phòng trống thì không có gì xảy ra.
 *
 * Cũng KHÔNG dùng violet/indigo: hai hue đó nay là màu thương hiệu (logo, vòng
 * focus, mục điều hướng đang chọn, nền Aurora). Chip mang màu brand thì người
 * dùng không đọc ra được nó là trạng thái hay là phần khung của app.
 */
/* `dot` là bản ĐẶC của cùng hue, dành cho chip lọc ở /phong: chip lọc chỉ có
   chỗ cho một chấm 6px, mà nền `-100` ở kích thước đó thì nhìn như chưa tô. */
export const statusOptions = [
    {
        value: 'available',
        label: 'Phòng trống',
        color: {bg: 'bg-slate-100 dark:bg-slate-500/20', text: 'text-slate-700 dark:text-slate-300'},
        dot: 'bg-slate-400'
    },
    {
        value: 'occupied',
        label: 'Đã cho thuê',
        color: {bg: 'bg-blue-100 dark:bg-blue-500/20', text: 'text-blue-800 dark:text-blue-300'},
        dot: 'bg-blue-500'
    },
    {
        value: 'moving_out',
        label: 'Sắp trống',
        color: {bg: 'bg-amber-100 dark:bg-amber-500/20', text: 'text-amber-800 dark:text-amber-300'},
        dot: 'bg-amber-500'
    },
    {
        value: 'family_occupied',
        label: 'Người nhà ở',
        color: {bg: 'bg-sky-100 dark:bg-sky-500/20', text: 'text-sky-800 dark:text-sky-300'},
        dot: 'bg-sky-500'
    }
];

/**
 * Trạng thái thanh toán của một tháng.
 *
 * `bg`/`text` mang cả biến thể tối. Trước đây chỉ có màu sáng (bg-amber-100…)
 * rồi thêm `dark:bg-opacity-80` ở chỗ hiển thị - cách đó không đổi được MÀU,
 * chỉ làm nó trong hơn, nên ở chế độ tối ra một con chip sáng trưng nằm trên
 * thẻ kính tối. Nền mờ theo kiểu `-500/15` + chữ `-300` mới khớp phần còn lại
 * của giao diện tối (xem TenantAvatar, glass-chip).
 */
/*
 * BA MÀU, KHÔNG PHẢI SÁU.
 * ---------------------------------------------------------------------------
 * Bảng này từng có sáu hue, mỗi trạng thái một màu, kèm cả một đoạn giải thích
 * hue nào nghĩa gì. Nếu phải viết chú thích để nhớ thì người dùng - vốn chỉ mở
 * app vài lần mỗi tháng và không có trí nhớ cơ bắp - chắc chắn không nhớ nổi.
 *
 * Câu hỏi duy nhất mà người ta hỏi khi mở app là "phòng nào chưa xong tiền?",
 * nên bảng màu chỉ cần trả lời đúng ba mức:
 *
 *   emerald = XONG        rose = CÓ VẤN ĐỀ        slate = CHƯA XONG
 *
 * Bốn trạng thái "chưa xong" phân biệt nhau bằng NHÃN CHỮ, không bằng hue. Chúng
 * chỉ khác nhau ở chỗ "ai đang phải làm gì tiếp" - mà một chấm màu 10px thì
 * không diễn đạt nổi việc đó, chỉ tạo cảm giác đã phân biệt được.
 *
 * Ngoại lệ duy nhất là `landlord_pending`: đó là trạng thái DUY NHẤT cần chính
 * chủ nhà ra tay, mà chủ nhà là người dùng chính. Nó đi nền slate ĐẶC thay vì
 * slate nhạt - vẫn cùng một hue nên không thêm màu phải học, nhưng nặng ký hơn
 * hẳn khi quét mắt. Đây cũng đúng quy ước đã dùng cho .glass-chip[data-active].
 *
 * Ràng buộc màu vẫn như cũ, nay thêm brand:
 *
 *   amber = ĐIỆN   sky = NƯỚC   emerald = ĐÃ THANH TOÁN
 *   violet/indigo = THƯƠNG HIỆU (logo, focus, nav, nền Aurora) - cấm dùng ở đây
 */
export const paymentStatusOptions = [
    {
        value: 'waiting_tenant_input',
        label: 'Chờ người thuê nhập',
        color: { bg: 'bg-slate-100 dark:bg-slate-500/20', text: 'text-slate-800 dark:text-slate-300' }
    },
    {
        value: 'landlord_pending',
        label: 'Chờ chủ nhà xác nhận',
        color: { bg: 'bg-slate-800 dark:bg-slate-100', text: 'text-white dark:text-slate-900' }
    },
    {
        value: 'landlord_rejected',
        label: 'Chủ nhà đã từ chối',
        color: { bg: 'bg-rose-100 dark:bg-rose-500/20', text: 'text-rose-800 dark:text-rose-300' }
    },
    {
        value: 'waiting_payment',
        label: 'Chờ người thuê thanh toán',
        color: { bg: 'bg-slate-100 dark:bg-slate-500/20', text: 'text-slate-800 dark:text-slate-300' }
    },
    {
        value: 'payment_submitted',
        label: 'Đã chuyển khoản chờ xác nhận',
        color: { bg: 'bg-slate-100 dark:bg-slate-500/20', text: 'text-slate-800 dark:text-slate-300' }
    },
    {
        value: 'paid',
        label: 'Đã thanh toán',
        color: { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-800 dark:text-emerald-300' }
    },
    {
        // Cùng hue emerald với 'paid': cả hai đều là XONG, khác nhau ở chỗ tiền
        // về bằng đường nào - việc đó chữ nói được, chấm màu 10px thì không.
        value: 'deducted_deposit',
        label: 'Đã trừ vào tiền cọc',
        color: { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-800 dark:text-emerald-300' }
    }
];

/**
 * Màu chấm trạng thái - bản rgb thô của bảng màu ngay trên.
 *
 * Phải viết rời vì chấm tô bằng biến CSS (`--status-color`, `backgroundColor`)
 * chứ không phải class Tailwind. Trước đây nó nằm trong RoomCardUtilities, nên
 * thanh chuyển phòng ở /diennuoc/:roomId muốn chấm cùng màu thì phải chép lại
 * lần nữa - ba bản chép của cùng một bảng màu. Nay chỉ còn một chỗ để sửa.
 *
 * Chỉ ba màu cho bảy trạng thái, cố ý: ở cỡ 8px thì hue không phân biệt nổi
 * trạng thái nào ra trạng thái nào, nó chỉ tạo cảm giác là đã phân biệt được.
 * Chữ trên chip mới làm việc đó.
 */
export const statusDotColor = (status?: string | null): string => {
    switch (status) {
        case 'landlord_pending':
            // Chấm ĐẬM hơn: đây là trạng thái duy nhất cần chính chủ nhà ra tay,
            // mà đây là màn hình của chủ nhà. slate-600 cố ý nằm ở khoảng giữa -
            // đủ tối để nổi trên nền kính sáng, đủ sáng để nổi trên nền kính tối,
            // nên nó là chấm "nặng" được ở CẢ HAI chế độ màu (chỉ có một biến màu,
            // không có biến thể tối riêng).
            return 'rgb(71 85 105)';   // slate-600
        case 'landlord_rejected':
            return 'rgb(251 113 133)'; // rose-400
        case 'paid':
        case 'deducted_deposit':
            return 'rgb(52 211 153)';  // emerald-400
        default:
            // Mọi trạng thái "chưa xong" và cả trạng thái lạ dùng chung chấm
            // trung tính: dữ liệu hỏng hiện ra trung tính thì tệ hơn nhiều so
            // với việc nó giả dạng một trạng thái có thật.
            return 'rgb(148 163 184)'; // slate-400
    }
};

export const bankingInfo = {
    accountName: "Nguyễn Xuân Thủy",
    accountNumber: "0081000303298",
    bankName: "Vietcombank",
    supportPhone: "0908729998",
    supportName: "Nguyễn Xuân Thủy (1960)"
} as const;

export const bankingInfoOther = {
    accountName: "Nguyễn Thị Minh Trang",
    accountNumber: "1234588995",
    bankName: "Techcombank",
    supportPhone: "0775109469",
    supportName: "Nguyễn Thị Minh Trang"
} as const;

// Đây là room sử dụng chuyển khoản cho banking 2
export const otherBankRooms = ['201', '202', '301', '302'];

// Hỗ trợ KỸ THUẬT (lỗi app), khác với SĐT chủ nhà ở bankingInfo dùng để hỏi
// chuyện tiền nong. Để một chỗ vì dùng cùng lúc ở trang đăng nhập lẫn menu
// tài khoản của người thuê.
export const SUPPORT_ZALO_PHONE = '0978754416';
export const supportZaloUrl = `https://zalo.me/${SUPPORT_ZALO_PHONE}`;

